import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Toggle from "../common/Toggle";
import { setHighContrast, setKeepAwake, setLyricSize, setShowChords, toggleDimRoom } from "../../actions/preferencesActions";
import { LYRIC_SIZE_LABELS, LYRIC_SIZES, isDarkTheme } from "../../lib/preferences";
import { transposeNote } from "../../lib/chords";
import { selectHostTranspose } from "../../selectors";

/**
 * The display settings a guest reaches from the Aa button without leaving the lyrics: the
 * lyric size, dim room, high contrast, chords and keep awake, and the key the host chose.
 */
const AaPopover = ({ song, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const preferences = useSelector((state) => state.preferences);
  const hostTranspose = useSelector((state) => selectHostTranspose(state, song.Id));
  const wakeLockSupported = typeof navigator !== "undefined" && Boolean(navigator.wakeLock);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const key = song.Key ? transposeNote(song.Key, hostTranspose) : null;

  return (
    <>
      <div className="aa-backdrop" onClick={onClose} role="presentation" />
      <div className="aa-pop" role="dialog" aria-label="Display settings" data-screen-label="Aa popover">
        <div className="aa-pop-head">
          <span className="aa-pop-title">Display</span>
          <button type="button" className="btn btn-round btn-xs btn-outline" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="aa-card">
          <div className="between" style={{ alignItems: "baseline", marginBottom: 12 }}>
            <span className="aa-label">Lyric size</span>
            <span className="aa-value">{LYRIC_SIZE_LABELS[preferences.lyricSize]}</span>
          </div>
          <div className="size-steps" role="radiogroup" aria-label="Lyric size">
            {LYRIC_SIZES.map((_, index) => (
              <button
                key={index}
                type="button"
                className="size-step"
                role="radio"
                aria-checked={preferences.lyricSize === index}
                aria-label={LYRIC_SIZE_LABELS[index]}
                style={{ fontSize: 13 + index * 4 }}
                onClick={() => dispatch(setLyricSize(index))}
              >
                A
              </button>
            ))}
          </div>
        </div>
        <Toggle label="Dim room" sub="Dark background, warm ink" on={isDarkTheme(preferences.theme)} onChange={() => dispatch(toggleDimRoom())} />
        <Toggle label="High contrast" sub="Pure black text, heavier weight" on={preferences.highContrast} onChange={(on) => dispatch(setHighContrast(on))} />
        <Toggle label="Chords above lyrics" sub="Only where the song has them" on={preferences.showChords} onChange={(on) => dispatch(setShowChords(on))} />
        <Toggle
          label="Keep screen awake"
          sub={wakeLockSupported ? "Until the session ends" : "Not supported on this device"}
          on={preferences.keepAwake && wakeLockSupported}
          onChange={(on) => dispatch(setKeepAwake(on))}
          disabled={!wakeLockSupported}
        />
        <div className="aa-foot">
          {key && (
            <>
              <div className="aa-key">Key {key} · set by the host</div>
              <div className="aa-key-sub">The host picks this for everyone</div>
            </>
          )}
          <button
            type="button"
            className="aa-more"
            onClick={() => {
              onClose();
              navigate("/settings");
            }}
          >
            All settings →
          </button>
        </div>
      </div>
    </>
  );
};

export default AaPopover;
